import classNames from 'classnames'
import React, { forwardRef, DetailedHTMLProps, useId } from 'react'
import { useTranslation } from 'react-i18next'
import sanitizeHtml from 'sanitize-html'
import { decodeHtmlEntities } from '@isdd/metais-common/utils/utils'
import { MODAL_TOOLTIP_ZINDEX } from '@isdd/metais-common/constants'

import { GreenCheckMarkIcon } from '@isdd/idsk-ui-kit/assets/images'
import { Tooltip } from '@isdd/idsk-ui-kit/tooltip/Tooltip'
import styles from '@isdd/idsk-ui-kit/styles/InfoAndCheckInput.module.scss'

interface IInputProps extends DetailedHTMLProps<React.InputHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> {
    id?: string
    label?: string | React.ReactNode
    name: string
    rows: number
    hint?: string
    error?: string
    disabled?: boolean
    info?: string
    correct?: boolean
    wrapperClassname?: string
    hasInputIcon?: boolean
    required?: boolean
    isInModal?: boolean
    announceErrorToScreenReader?: boolean
}

export const TextArea = forwardRef<HTMLTextAreaElement, IInputProps>(
    (
        {
            id,
            label,
            name,
            rows,
            hint,
            info,
            error,
            disabled,
            correct,
            wrapperClassname,
            hasInputIcon = false,
            required,
            isInModal = false,
            announceErrorToScreenReader = false,
            ...rest
        },
        ref,
    ) => {
        const uniqueId = useId()
        const inputId = id ?? `input-${uniqueId}` // NVDA has problems with ID starting with `useID` `::`, needs to be prefixed
        const hintId = `${inputId}-hint`
        const errorId = `${inputId}-error`
        const { t } = useTranslation()
        return (
            <div className={classNames('govuk-form-group', wrapperClassname, { 'govuk-form-group--error': !!error })}>
                <div className={styles.labelDiv}>
                    {label && (
                        <label className={classNames('govuk-label', styles.wrap)} htmlFor={inputId}>
                            {label} {required && t('input.requiredField')}
                        </label>
                    )}
                    {info && (
                        <Tooltip
                            altText={t('tooltip', { tooltip: label })}
                            descriptionElement={
                                <div className="tooltipWidth500">
                                    {
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: sanitizeHtml(decodeHtmlEntities(info)),
                                            }}
                                        />
                                    }
                                </div>
                            }
                            contentStyle={isInModal ? { zIndex: MODAL_TOOLTIP_ZINDEX } : undefined}
                        />
                    )}
                </div>
                <span id={hintId} className={classNames({ 'govuk-visually-hidden': !hint, 'govuk-hint': !!hint })}>
                    {hint}
                </span>

                <span
                    id={errorId}
                    role={announceErrorToScreenReader ? 'alert' : undefined}
                    className={classNames({ 'govuk-visually-hidden': !error, 'govuk-error-message': !!error })}
                >
                    {error && <span className="govuk-visually-hidden">{t('error')}</span>}
                    {error}
                </span>
                <div className={styles.inputWrapper}>
                    <textarea
                        className={classNames('govuk-textarea', { ' govuk-textarea--error': !!error })}
                        id={inputId}
                        name={name}
                        rows={rows}
                        ref={ref}
                        {...rest}
                        readOnly={disabled}
                        aria-invalid={!!error}
                        aria-describedby={`${hintId} ${errorId}`}
                        aria-errormessage={errorId}
                        required={required}
                    />
                    {correct && (
                        <img src={GreenCheckMarkIcon} className={hasInputIcon ? styles.isCorrectWithIcon : styles.isCorrect} alt={t('valid')} />
                    )}
                </div>
            </div>
        )
    },
)
